// DTOs/schemas do módulo "plataforma" (área do SUPERADMIN). Este arquivo pertence ao pacote `plataforma`.
// Ids que cruzam a API são sempre `publicId`. Valores monetários em centavos de real.
import { z } from "zod";

import { STATUS_CONTA, STATUS_NUMERO, TIPOS_NUMERO } from "./enums";
import type { StatusConta, StatusNumero, TipoNumero } from "./enums";
import type { FaturaResumo } from "./planos";

// ─── Paginação ───────────────────────────────────────────────────────────────

export const zPaginacaoAdmin = z.object({
  pagina: z.coerce.number().int().min(1).default(1),
  tamanho: z.coerce.number().int().min(1).max(100).default(20),
});

export interface PaginaAdmin<T> {
  itens: T[];
  total: number;
  pagina: number;
  tamanho: number;
}

const zIdPublico = z.string().trim().min(1).max(40);
const zBuscaOpcional = z
  .string()
  .trim()
  .max(120)
  .optional()
  .transform((v) => (v ? v : undefined));

// ─── Auditoria ───────────────────────────────────────────────────────────────

const zDataConsulta = z
  .string()
  .trim()
  .refine((v) => !Number.isNaN(Date.parse(v)), "Data inválida.");

export const zConsultaAuditoria = z.object({
  /** publicId da conta alvo. */
  contaId: zIdPublico.optional(),
  /** publicId do usuário autor. */
  autorId: zIdPublico.optional(),
  /** Ação exata (`conta.estender_trial`) ou prefixo terminado em ponto (`conta.`). */
  acao: z.string().trim().min(1).max(80).optional(),
  /** ISO ou `YYYY-MM-DD` (início do dia, inclusive). */
  de: zDataConsulta.optional(),
  /** ISO ou `YYYY-MM-DD` (fim do dia, inclusive). */
  ate: zDataConsulta.optional(),
  ordem: z.enum(["asc", "desc"]).default("desc"),
  limite: z.coerce.number().int().min(1).max(100).default(50),
  cursor: z.string().trim().min(1).max(60).optional(),
});
export type ConsultaAuditoria = z.infer<typeof zConsultaAuditoria>;

export interface RegistroAuditoria {
  /** Opaco (serve de cursor). */
  id: string;
  /** publicId do autor (o SUPERADMIN, mesmo durante a impersonação). */
  autorId: string | null;
  autorEmail: string | null;
  acao: string;
  alvo: string | null;
  contaAlvo: { id: string; nome: string } | null;
  /** Resumo do que mudou; nunca contém segredos. */
  diff: Record<string, unknown> | null;
  ip: string | null;
  criadoEm: string;
}

export interface ListaAuditoria {
  itens: RegistroAuditoria[];
  proximoCursor: string | null;
}

// ─── Visão geral ─────────────────────────────────────────────────────────────

export interface ContagemCanalNumeros {
  total: number;
  /** `OFICIAL`: status ATIVO. `NAO_OFICIAL`: status CONECTADO. */
  ativos: number;
}

export interface VisaoGeralPlataforma {
  geradoEm: string;
  /** Soma do preço do plano das contas `active`. */
  mrrCentavos: number;
  totalContas: number;
  contasPorStatus: Record<StatusConta, number>;
  numeros: Record<TipoNumero, ContagemCanalNumeros>;
  economiaMes: {
    /** `YYYY-MM` (America/Sao_Paulo). */
    mes: string;
    marketingCentavos: number;
    utilityCentavos: number;
    totalCentavos: number;
  };
  mensagens7d: {
    enviadas: number;
    recebidas: number;
    porDia: Array<{ dia: string; enviadas: number; recebidas: number }>;
  };
  novasContas30d: {
    total: number;
    /** 30 dias corridos, do mais antigo ao de hoje (zeros incluídos). */
    porDia: Array<{ dia: string; qtd: number }>;
  };
}

// ─── Contas ──────────────────────────────────────────────────────────────────

export const zConsultaContasAdmin = zPaginacaoAdmin.extend({
  busca: zBuscaOpcional,
  status: z.enum(STATUS_CONTA).optional(),
  /** publicId do plano. */
  planoId: zIdPublico.optional(),
});
export type ConsultaContasAdmin = z.infer<typeof zConsultaContasAdmin>;

export interface ContaAdminResumo {
  id: string;
  nome: string;
  email: string;
  documento: string | null;
  status: StatusConta;
  plano: { id: string; nome: string; precoCentavos: number } | null;
  trialEndsAt: string | null;
  numeros: number;
  criadoEm: string;
}

export interface NumeroAdminResumo {
  id: string;
  tipo: TipoNumero;
  apelido: string;
  status: StatusNumero;
  telefone: string | null;
  ultimaAtividadeEm: string | null;
  desconectadoEm: string | null;
  /** Já removido pelo cliente (instância ainda por apagar no Evolution). */
  removido: boolean;
  criadoEm: string;
}

export interface NumeroGlobal extends NumeroAdminResumo {
  conta: { id: string; nome: string };
  /** Só NAO_OFICIAL: proxy da instância (sem a senha). */
  proxy: ProxyResumo | null;
}

export const PROTOCOLOS_PROXY = ["http", "https", "socks5"] as const;

export const zProxyNumero = z.object({
  protocolo: z.enum(PROTOCOLOS_PROXY),
  host: z
    .string()
    .trim()
    .min(1, "Informe o host.")
    .max(253)
    .regex(/^[a-z0-9.-]+$/i, "Host inválido (só o nome ou IP, sem http://)."),
  porta: z.number().int().min(1).max(65535),
  usuario: z.string().trim().max(200).optional(),
  senha: z.string().max(500).optional(),
});
export type ProxyNumero = z.infer<typeof zProxyNumero>;

export interface ProxyResumo {
  protocolo: (typeof PROTOCOLOS_PROXY)[number];
  host: string;
  porta: number;
  usuario: string | null;
  temSenha: boolean;
}

export interface ContaAdminDetalhe extends ContaAdminResumo {
  telefone: string | null;
  pastDueDesde: string | null;
  suspensaEm: string | null;
  canceladaEm: string | null;
  assinada: boolean;
  owner: { nome: string; email: string; ativo: boolean; ultimoLoginEm: string | null } | null;
  numerosLista: NumeroAdminResumo[];
  uso: {
    numeros: { usados: number; limite: number };
    mensagens30d: { enviadas: number; recebidas: number };
    economiaMes: { marketingCentavos: number; utilityCentavos: number };
  };
  faturas: FaturaResumo[];
}

export const zTrocarPlanoAdmin = z.object({
  planoId: zIdPublico,
  /** Atualiza o valor da assinatura no Asaas (se a conta tiver uma). */
  sincronizarAsaas: z.boolean().default(true),
});
export type TrocarPlanoAdmin = z.infer<typeof zTrocarPlanoAdmin>;

export const zEstenderTrial = z.object({
  dias: z.number().int().min(1).max(365),
});
export type EstenderTrial = z.infer<typeof zEstenderTrial>;

export const zAlterarStatusConta = z.object({
  status: z.enum(["active", "suspended"]),
  motivo: z.string().trim().max(300).optional(),
});
export type AlterarStatusConta = z.infer<typeof zAlterarStatusConta>;

export interface RespostaImpersonacao {
  accessToken: string;
  /** ISO. No máximo 30 minutos a partir de agora. */
  expiraEm: string;
  expiraEmSegundos: number;
  conta: { id: string; nome: string };
  /** Texto do banner fixo exibido durante a sessão. */
  aviso: string;
}

export const zEncerrarImpersonacao = z.object({
  /** Só quando um SUPERADMIN (sem token de impersonação) registra o fim. */
  contaId: zIdPublico.optional(),
});
export type EncerrarImpersonacao = z.infer<typeof zEncerrarImpersonacao>;

// ─── Planos ──────────────────────────────────────────────────────────────────

const zRecursos = z.array(z.string().trim().min(1).max(200)).max(30);

export const zCriarPlanoAdmin = z.object({
  nome: z.string().trim().min(2).max(60),
  precoCentavos: z.number().int().min(0).max(100_000_00),
  limiteNumeros: z.number().int().min(1).max(10_000),
  recursos: zRecursos.default([]),
  recomendado: z.boolean().default(false),
  ativo: z.boolean().default(true),
  ordem: z.number().int().min(0).max(10_000).optional(),
});
export type CriarPlanoAdmin = z.infer<typeof zCriarPlanoAdmin>;

export const zAtualizarPlanoAdmin = z
  .object({
    nome: z.string().trim().min(2).max(60),
    precoCentavos: z.number().int().min(0).max(100_000_00),
    limiteNumeros: z.number().int().min(1).max(10_000),
    recursos: zRecursos,
    recomendado: z.boolean(),
    ativo: z.boolean(),
    ordem: z.number().int().min(0).max(10_000),
    /**
     * Confirmação EXPLÍCITA: atualiza o valor da assinatura de cada conta do plano
     * no Asaas. Sem ela, o novo preço vale só para assinaturas novas.
     */
    aplicarAssinaturasExistentes: z.boolean(),
  })
  .partial();
export type AtualizarPlanoAdmin = z.infer<typeof zAtualizarPlanoAdmin>;

export const zOrdenarPlanos = z.object({
  /** publicIds na ordem desejada. */
  ids: z.array(zIdPublico).min(1).max(100),
});
export type OrdenarPlanos = z.infer<typeof zOrdenarPlanos>;

export interface PlanoAdmin {
  id: string;
  nome: string;
  precoCentavos: number;
  limiteNumeros: number;
  recursos: string[];
  recomendado: boolean;
  ativo: boolean;
  ordem: number;
  /** Contas que estão neste plano (qualquer status exceto canceladas). */
  assinantes: number;
  criadoEm: string;
}

export interface FalhaAplicacaoPreco {
  /** publicId da conta. */
  contaId: string;
  nome: string;
  motivo: string;
}

export interface ResultadoAplicacaoPreco {
  total: number;
  atualizadas: number;
  falhas: FalhaAplicacaoPreco[];
}

export interface RespostaAtualizarPlano {
  plano: PlanoAdmin;
  /** Presente só quando `aplicarAssinaturasExistentes` foi enviado como `true`. */
  aplicacao: ResultadoAplicacaoPreco | null;
}

// ─── Configurações ───────────────────────────────────────────────────────────

export const zAtualizarConfiguracoes = z.object({
  asaas: z
    .object({
      ambiente: z.enum(["sandbox", "producao"]).optional(),
      /** Vazio ou ausente = manter o atual. */
      apiKey: z.string().max(500).optional(),
      webhookToken: z.string().max(500).optional(),
    })
    .optional(),
  evolution: z
    .object({
      url: z
        .string()
        .trim()
        .max(300)
        .refine((v) => /^https?:\/\/[^\s]+$/i.test(v), "Informe uma URL http:// ou https://.")
        .nullable()
        .optional(),
      globalKey: z.string().max(500).optional(),
    })
    .optional(),
  meta: z
    .object({
      graphVersion: z
        .string()
        .trim()
        .regex(/^v\d{1,2}\.\d{1,2}$/, "Use o formato v21.0.")
        .optional(),
    })
    .optional(),
  smtp: z
    .object({
      host: z.string().trim().max(200).nullable().optional(),
      port: z.number().int().min(1).max(65535).nullable().optional(),
      user: z.string().trim().max(200).nullable().optional(),
      pass: z.string().max(500).optional(),
      from: z.string().trim().max(200).nullable().optional(),
    })
    .optional(),
  r2: z
    .object({
      accountId: z.string().trim().regex(/^[a-f0-9]{32}$/i, "O Account ID tem 32 caracteres hexadecimais.").nullable().optional(),
      bucket: z
        .string()
        .trim()
        .regex(/^[a-z0-9][a-z0-9-]{1,61}[a-z0-9]$/, "Nome de bucket inválido (minúsculas, números e hífen).")
        .nullable()
        .optional(),
      accessKeyId: z.string().trim().max(200).nullable().optional(),
      secretKey: z.string().max(500).optional(),
    })
    .optional(),
  /** Proxy aplicado aos números não oficiais criados depois; `null` remove. Senha vazia mantém a atual. */
  proxyPadrao: zProxyNumero.nullable().optional(),
  limiteVerificacoesHora: z.number().int().min(10).max(100_000).optional(),
  limiteGruposCriadosDia: z.number().int().min(0).max(1_000).optional(),
  limiteParticipantesAdicionadosDia: z.number().int().min(0).max(10_000).optional(),
  limiteStatusDia: z.number().int().min(0).max(1_000).optional(),
  google: z
    .object({
      /** OAuth Client ID (tipo Web). Vazio/null desliga o login com Google. */
      clientId: z
        .string()
        .trim()
        .max(200)
        .regex(/\.apps\.googleusercontent\.com$/, "Client ID inválido (termina em .apps.googleusercontent.com).")
        .nullable()
        .optional(),
    })
    .optional(),
  retencaoDiasMensagens: z.number().int().min(1).max(3650).optional(),
  trialDias: z.number().int().min(0).max(365).optional(),
});
export type AtualizarConfiguracoes = z.infer<typeof zAtualizarConfiguracoes>;

/** Segredos SEMPRE mascarados (`••••1234`). */
export interface ConfiguracoesPlataforma {
  asaas: {
    ambiente: string;
    apiKey: string | null;
    webhookToken: string | null;
    /** URL que o Asaas deve chamar (registrada pelo botão "registrar webhook"). */
    webhookUrl: string;
  };
  evolution: { url: string | null; globalKey: string | null };
  meta: { graphVersion: string };
  smtp: {
    host: string | null;
    port: number | null;
    user: string | null;
    pass: string | null;
    from: string | null;
  };
  /** Cloudflare R2 (armazenamento das mídias). `secretKey` mascarado. */
  r2: { accountId: string | null; bucket: string | null; accessKeyId: string | null; secretKey: string | null };
  proxyPadrao: ProxyResumo | null;
  limiteVerificacoesHora: number;
  limiteGruposCriadosDia: number;
  limiteParticipantesAdicionadosDia: number;
  limiteStatusDia: number;
  google: { clientId: string | null };
  retencaoDiasMensagens: number;
  trialDias: number;
  atualizadoEm: string;
  /** Alertas de configuração (ex.: chave global padrão do Evolution). */
  avisos: string[];
}

export const zTestarEmail = z.object({
  /** Destino do teste; por padrão, o e-mail do próprio SUPERADMIN. */
  para: z.string().trim().email().max(200).optional(),
});
export type TestarEmail = z.infer<typeof zTestarEmail>;

export type ResultadoTeste =
  | { ok: true; latenciaMs?: number; detalhe?: string; avisos?: string[] }
  | { ok: false; erro: string; avisos?: string[] };

export interface ResultadoRegistroWebhook {
  ok: true;
  url: string;
  eventos: string[];
  /** O token ainda não existia: foi gerado e salvo (criptografado) agora. */
  tokenGerado: boolean;
  /** Já havia um webhook para esta URL no Asaas: foi atualizado em vez de duplicado. */
  atualizado: boolean;
}

// ─── Números globais ────────────────────────────────────────────────────────

export const zConsultaNumerosGlobais = zPaginacaoAdmin.extend({
  /** Apelido, telefone ou nome da conta. */
  busca: zBuscaOpcional,
  tipo: z.enum(TIPOS_NUMERO).optional(),
  status: z.enum(STATUS_NUMERO).optional(),
  /** publicId da conta. */
  contaId: zIdPublico.optional(),
  incluirRemovidos: z
    .enum(["true", "false"])
    .default("false")
    .transform((v) => v === "true"),
});
export type ConsultaNumerosGlobais = z.infer<typeof zConsultaNumerosGlobais>;

export interface InstanciaOrfa {
  /** Id (UUID) da instância no Evolution. */
  instanceId: string;
  name: string;
  /** Sessão do WhatsApp ativa nessa instância. */
  logada: boolean;
}

export interface ListaInstanciasOrfas {
  orfas: InstanciaOrfa[];
  /** Total de instâncias `zh_*` no Evolution (para a tela mostrar o contexto). */
  totalInstanciasZaphub: number;
}
